import { Component, Input, Inject, OnChanges, SimpleChanges, Output, EventEmitter, HostListener, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { AdminService, Employee, EmployeeSalary, EmployeeSalaryConsistencyIssue } from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { DialogService } from '../../../core/services/dialog.service';
import { ModalService } from '../../../core/services/modal.service';
import { EmployeeSalaryMassUpdateModalComponent } from '../../../shared/components/modal/employee-salary-mass-update-modal.component';

type EmployeeSalaryEditModel = Partial<EmployeeSalary>;

@Component({
  selector: 'app-admin-salary-tab',
  standalone: false,
  template: require('./admin-salary-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-salary-tab.component.scss').default],
})
export class AdminSalaryTabComponent implements OnChanges {
  private readonly requiredCreateFields: Array<keyof EmployeeSalaryEditModel> = ['startdate', 'salary'];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.btn-group')) {
      this.showMoreMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showMoreMenu = false;
  }

  @Input() employees: Employee[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  activeFilter: 'all' | 'active' | 'inactive' = 'active';
  selectableEmployees: Employee[] = [];
  selectedEmployee: Employee | null = null;
  salaryRows: EmployeeSalary[] = [];
  currentIndex = 0;
  selectedSalaryRow: EmployeeSalary | null = null;
  editSalaryRow: EmployeeSalaryEditModel = {};
  initialEditSalaryRow: EmployeeSalaryEditModel = {};
  originalId: number | null = null;

  startDateMoment: moment.Moment | null = null;
  stopDateMoment: moment.Moment | null = null;

  isNew = false;
  isDirty = false;
  showMoreMenu = false;
  loading = false;
  consistencyLoading = false;
  showConsistencyDetails = false;
  consistencyIssues: EmployeeSalaryConsistencyIssue[] = [];
  private pendingSelectionId: number | null = null;
  private pendingSelectionIndex: number | null = null;
  private hasLoadedConsistency = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService,
    @Inject(ModalService) private modalService: ModalService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['employees']) {
      this.rebuildSelectableEmployees();
      const availableEmployees = this.selectableEmployees;

      if (availableEmployees.length === 0) {
        this.selectedEmployee = null;
        this.salaryRows = [];
        this.selectedSalaryRow = null;
        this.editSalaryRow = {};
      } else if (!this.selectedEmployee || !availableEmployees.some((employee) => employee.employee_id === this.selectedEmployee?.employee_id)) {
        this.selectedEmployee = availableEmployees[0];
        this.onEmployeeChange();
      }
    }

    if (changes['employees'] && !this.consistencyLoading) {
      this.refreshConsistency();
    }
  }

  setActiveFilter(filter: 'all' | 'active' | 'inactive'): void {
    if (this.activeFilter === filter) return;
    this.activeFilter = filter;
    this.rebuildSelectableEmployees();
    const availableEmployees = this.selectableEmployees;
    this.selectedEmployee = availableEmployees.length > 0 ? availableEmployees[0] : null;
    this.onEmployeeChange();
  }

  get hasConsistencyIssues(): boolean {
    return this.consistencyIssues.length > 0;
  }

  get issuesForSelectedEmployee(): EmployeeSalaryConsistencyIssue[] {
    if (!this.selectedEmployee) return [];
    return this.consistencyIssues.filter((issue) => issue.employee_id === this.selectedEmployee?.employee_id);
  }

  get sortedConsistencyIssues(): EmployeeSalaryConsistencyIssue[] {
    const source = this.issuesForSelectedEmployee.length > 0 ? this.issuesForSelectedEmployee : this.consistencyIssues;
    return [...source].sort((left, right) => {
      const employeeCompare = left.employee_name.localeCompare(right.employee_name, 'sv');
      if (employeeCompare !== 0) return employeeCompare;
      const startCompare = String(left.startdate || '').localeCompare(String(right.startdate || ''));
      if (startCompare !== 0) return startCompare;
      return left.type.localeCompare(right.type, 'sv');
    });
  }

  get historyRows(): EmployeeSalary[] {
    return [...this.salaryRows].sort((a, b) => {
      const startCompare = String(a.startdate).localeCompare(String(b.startdate));
      if (startCompare !== 0) return startCompare;
      return a.record_id - b.record_id;
    });
  }

  get hasEditableSalaryRow(): boolean {
    return this.isNew || this.originalId !== null;
  }

  refreshConsistency(): void {
    this.consistencyLoading = true;
    this.adminService.getEmployeeSalaryConsistency().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.consistencyIssues = result.issues || [];
        this.hasLoadedConsistency = true;
        if (!this.hasConsistencyIssues) {
          this.showConsistencyDetails = false;
        }
        this.consistencyLoading = false;
      },
      error: (err) => {
        console.error('Error loading employee salary consistency', err);
        this.consistencyIssues = [];
        this.hasLoadedConsistency = true;
        this.consistencyLoading = false;
      },
    });
  }

  onEmployeeChange(): void {
    if (!this.selectedEmployee) {
      this.salaryRows = [];
      this.selectedSalaryRow = null;
      this.editSalaryRow = {};
      return;
    }

    this.loading = true;
    this.adminService.getEmployeeSalaries(this.selectedEmployee.employee_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows) => {
        this.salaryRows = rows;
        this.loading = false;
        this.applySelectionAfterLoad();
      },
      error: (err) => {
        this.loading = false;
        this.adminFeedback.showCrudError('uppdatera', 'lönerader', err);
      },
    });
  }

  applySelectionAfterLoad(): void {
    if (this.isNew || this.isDirty) {
      if (!this.isNew && this.selectedSalaryRow) {
        const selectedIndex = this.salaryRows.findIndex((row) => row.record_id === this.selectedSalaryRow?.record_id);
        this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
      } else if (this.salaryRows.length === 0) {
        this.currentIndex = 0;
      } else {
        this.currentIndex = Math.min(this.currentIndex, this.salaryRows.length - 1);
      }

      this.pendingSelectionId = null;
      this.pendingSelectionIndex = null;
      return;
    }

    if (this.salaryRows.length > 0) {
      const preferredId = this.pendingSelectionId ?? this.originalId ?? this.selectedSalaryRow?.record_id ?? null;
      const preferredIndex = preferredId === null ? -1 : this.salaryRows.findIndex((row) => row.record_id === preferredId);

      if (preferredIndex >= 0) {
        this.currentIndex = preferredIndex;
        this.selectSalaryRow(this.salaryRows[preferredIndex]);
      } else if (this.pendingSelectionIndex !== null) {
        const clampedIndex = Math.min(this.pendingSelectionIndex, this.salaryRows.length - 1);
        this.currentIndex = clampedIndex;
        this.selectSalaryRow(this.salaryRows[clampedIndex]);
      } else {
        this.currentIndex = 0;
        this.selectSalaryRow(this.salaryRows[0]);
      }
    } else {
      this.selectedSalaryRow = null;
      this.editSalaryRow = {};
      this.initialEditSalaryRow = {};
      this.startDateMoment = null;
      this.stopDateMoment = null;
      this.currentIndex = 0;
    }

    this.pendingSelectionId = null;
    this.pendingSelectionIndex = null;
  }

  selectSalaryRow(row: EmployeeSalary): void {
    this.selectedSalaryRow = row;
    this.originalId = row.record_id;
    this.editSalaryRow = { ...row };
    this.initialEditSalaryRow = { ...row };
    this.startDateMoment = row.startdate ? moment(row.startdate) : null;
    this.stopDateMoment = row.stopdate ? moment(row.stopdate) : null;
    this.isNew = false;
    this.isDirty = false;
  }

  selectSalaryRowFromTable(row: EmployeeSalary): void {
    const index = this.salaryRows.findIndex((item) => item.record_id === row.record_id);
    if (index >= 0) {
      this.currentIndex = index;
    }
    this.selectSalaryRow(row);
  }

  addNew(): void {
    if (!this.selectedEmployee) return;

    this.isNew = true;
    this.selectedSalaryRow = null;
    this.originalId = null;
    this.editSalaryRow = {
      employee_id: this.selectedEmployee.employee_id,
      startdate: new Date().toISOString().slice(0, 10),
      stopdate: null,
      salary: 0,
      bonus: 0,
      overtime: 0,
      cbonus: 0,
      responsability: 0,
      time: 1,
    };
    this.initialEditSalaryRow = { ...this.editSalaryRow };
    this.startDateMoment = this.editSalaryRow.startdate ? moment(this.editSalaryRow.startdate) : null;
    this.stopDateMoment = null;
    this.isDirty = false;
  }

  onStartDateChange(newDate: moment.Moment): void {
    this.startDateMoment = newDate;
    this.editSalaryRow.startdate = newDate ? newDate.format('YYYY-MM-DD') : undefined;
    this.onFieldChange();
  }

  onStopDateChange(newDate: moment.Moment): void {
    this.stopDateMoment = newDate;
    this.editSalaryRow.stopdate = newDate ? newDate.format('YYYY-MM-DD') : null;
    this.onFieldChange();
  }

  save(): void {
    const payload = {
      ...this.editSalaryRow,
      employee_id: this.selectedEmployee?.employee_id,
    };

    if (this.isNew) {
      this.adminService.createEmployeeSalary(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          this.pendingSelectionId = result?.insertId ?? null;
          this.isNew = false;
          this.isDirty = false;
          this.onEmployeeChange();
          this.refreshConsistency();
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('skapa', 'lönerad', err),
      });
    } else if (this.originalId !== null) {
      this.adminService.updateEmployeeSalary(this.originalId, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.pendingSelectionId = this.originalId;
          this.isDirty = false;
          this.onEmployeeChange();
          this.refreshConsistency();
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('uppdatera', 'lönerad', err),
      });
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.isNew = false;
      if (this.salaryRows.length > 0) {
        this.selectSalaryRow(this.salaryRows[this.currentIndex]);
      } else {
        this.editSalaryRow = {};
        this.initialEditSalaryRow = {};
        this.startDateMoment = null;
        this.stopDateMoment = null;
      }
    } else if (this.selectedSalaryRow) {
      this.selectSalaryRow(this.selectedSalaryRow);
      return;
    }
    this.isDirty = false;
  }

  async delete(): Promise<void> {
    if (this.originalId === null) return;

    const confirmed = await this.dialogService.confirm({
      title: 'Bekräfta borttagning',
      body: 'Är du säker på att du vill ta bort denna lönerad?',
      confirmText: 'Ta bort',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) return;

    this.pendingSelectionIndex = this.currentIndex;
    this.adminService.deleteEmployeeSalary(this.originalId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.onEmployeeChange();
        this.refreshConsistency();
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('ta bort', 'lönerad', err),
    });
  }

  duplicate(): void {
    if (!this.selectedSalaryRow || !this.selectedEmployee) return;
    this.isNew = true;
    this.originalId = null;
    this.editSalaryRow = {
      ...this.selectedSalaryRow,
      record_id: undefined,
      employee_id: this.selectedEmployee.employee_id,
      startdate: new Date().toISOString().slice(0, 10),
      stopdate: null,
    };
    this.initialEditSalaryRow = { ...this.selectedSalaryRow };
    this.startDateMoment = this.editSalaryRow.startdate ? moment(this.editSalaryRow.startdate) : null;
    this.stopDateMoment = null;
    this.isDirty = true;
  }

  async openMassUpdate(): Promise<void> {
    const result = await this.modalService.show(EmployeeSalaryMassUpdateModalComponent);
    if (!result?.ok || !result?.applied) {
      return;
    }

    this.onEmployeeChange();
    this.refreshConsistency();
    this.dataUpdated.emit();
  }

  onFieldChange(): void {
    this.isDirty = this.hasChanges();
  }

  isRequiredField(field: keyof EmployeeSalaryEditModel): boolean {
    return this.requiredCreateFields.includes(field);
  }

  isRequiredMissing(field: keyof EmployeeSalaryEditModel): boolean {
    if (!this.isNew || !this.isRequiredField(field)) return false;
    const value = this.editSalaryRow[field];
    return typeof value === 'string' ? value.trim() === '' : value == null;
  }

  hasMissingRequiredFields(): boolean {
    return this.requiredCreateFields.some((field) => this.isRequiredMissing(field));
  }

  isFieldDirty(field: keyof EmployeeSalaryEditModel): boolean {
    return this.normalizeFieldValue(this.editSalaryRow[field]) !== this.normalizeFieldValue(this.initialEditSalaryRow[field]);
  }

  hasChanges(): boolean {
    const fields = new Set<keyof EmployeeSalaryEditModel>([
      ...Object.keys(this.editSalaryRow),
      ...Object.keys(this.initialEditSalaryRow),
    ] as Array<keyof EmployeeSalaryEditModel>);
    return Array.from(fields).some((field) => this.isFieldDirty(field));
  }

  private normalizeFieldValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  canSave(): boolean {
    if (!this.isDirty) return false;
    if (!this.isNew) return true;
    return !this.hasMissingRequiredFields();
  }

  canAddNew(): boolean {
    return !this.isNew && !this.isDirty && !!this.selectedEmployee;
  }

  canUndo(): boolean {
    return this.isDirty || this.isNew;
  }

  canOpenMoreMenu(): boolean {
    return !this.isNew && !this.isDirty && this.originalId !== null;
  }

  canOpenMassUpdate(): boolean {
    return !this.isNew && !this.isDirty;
  }

  getConsistencyStatusText(): string {
    if (this.consistencyLoading && !this.hasLoadedConsistency) {
      return 'Kontrollerar lönehistorik...';
    }

    const currentIssues = this.issuesForSelectedEmployee;
    if (this.selectedEmployee && currentIssues.length === 0) {
      return `${this.selectedEmployee.name} har konsistent lönehistorik.`;
    }

    if (this.selectedEmployee && currentIssues.length > 0) {
      return `${currentIssues.length} konsistensproblem hittades för ${this.selectedEmployee.name}.`;
    }

    if (!this.hasConsistencyIssues) {
      return 'Lönehistoriken är konsistent.';
    }

    return `${this.consistencyIssues.length} konsistensproblem hittades i lönehistoriken.`;
  }

  toggleConsistencyDetails(): void {
    this.showConsistencyDetails = !this.showConsistencyDetails;
  }

  getIssueRowKey(_index: number, issue: EmployeeSalaryConsistencyIssue): string {
    return [issue.type, issue.employee_id, issue.startdate || '', issue.stopdate || ''].join(':');
  }

  private rebuildSelectableEmployees(): void {
    this.selectableEmployees = this.employees
      .filter((employee) => {
        if (employee.employee_id === 0) return false;
        if (String(employee.contractor || '').toUpperCase() === 'J') return false;
        if (this.activeFilter === 'all') return true;
        if (this.activeFilter === 'active') return !employee.stop_date;
        return !!employee.stop_date;
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));
  }
}
